/**
 * Planning a FOLDER transfer: walk the tree, decide what gets created where,
 * and refuse what must not be written.
 *
 * Pure and injectable — the listing functions are passed in — because every
 * interesting property here is about the walk, not about the network, and each
 * of them is a way to hang the app or write somewhere the user did not choose.
 *
 * The plan is computed IN FULL before a single byte moves. That is what lets a
 * too-large tree be refused cleanly instead of half-transferred, and it is what
 * lets the caller create every directory before the first file needs one.
 */

/** Past this many files a folder transfer is refused rather than queued. Each
 *  file is a transfer row; a few thousand of them make the strip useless and
 *  a mistaken drop of a home directory unrecoverable to cancel. */
export const MAX_FILES = 2000;

/** A real tree is never this deep. A link loop that slipped past the symlink
 *  check is, so this is the backstop that turns "hangs forever" into an error. */
export const MAX_DEPTH = 32;

export class FolderTooLargeError extends Error {
  constructor(kind, limit) {
    super(kind === 'depth' ? `deeper than ${limit} levels` : `more than ${limit} files`);
    this.name = 'FolderTooLargeError';
    this.kind = kind;
    this.limit = limit;
  }
}

/**
 * Can this name be written as ONE path segment on the destination?
 *
 * Names come from the OTHER side and are untrusted. On the local disk that
 * matters most: a remote file called `..\..\evil` is a legal POSIX name, and on
 * Windows the backslash is a separator. `C:x` is drive-relative. A NUL ends the
 * string early in native code. Rust's containment check is the real guard; this
 * is the reason a bad name is SKIPPED and reported rather than sent to Rust to
 * be refused mid-transfer.
 */
export function isSafeSegment(name) {
  if (typeof name !== 'string' || name.length === 0) return false;
  if (name === '.' || name === '..') return false;
  // eslint-disable-next-line no-control-regex
  if (/[/\\: ]/.test(name)) return false;
  return true;
}

/**
 * Walk a tree from `root` and produce a plan.
 *
 * @param {(path: string) => Promise<Array<{name, path, isDir, isSymlink, size}>>} list
 * @param {string} root            source directory to walk
 * @param {string} destRoot        destination directory the tree lands under
 * @param {(dir: string, name: string) => string} joinDest  destination path joiner
 * @returns {Promise<{dirs: string[], files: Array<{from, to, name, size}>, skipped: string[]}>}
 */
export async function planFolder(list, root, destRoot, joinDest, limits = {}) {
  const maxFiles = limits.maxFiles ?? MAX_FILES;
  const maxDepth = limits.maxDepth ?? MAX_DEPTH;
  const dirs = [destRoot];
  const files = [];
  const skipped = [];

  const walk = async (src, dest, depth) => {
    if (depth > maxDepth) throw new FolderTooLargeError('depth', maxDepth);
    const entries = await list(src);
    for (const entry of entries || []) {
      if (!isSafeSegment(entry.name)) {
        skipped.push(entry.name);
        continue;
      }
      const to = joinDest(dest, entry.name);
      if (entry.isDir) {
        // **Symlinked directories are not followed.** A link to a parent makes
        // the walk infinite, and the local listing reports a symlinked folder as
        // a folder. Following none of them is the only rule that cannot loop.
        if (entry.isSymlink) {
          skipped.push(entry.name);
          continue;
        }
        dirs.push(to);
        // eslint-disable-next-line no-await-in-loop
        await walk(entry.path, to, depth + 1);
      } else {
        files.push({ from: entry.path, to, name: entry.name, size: Number(entry.size) || 0 });
        // Checked as the plan grows, not at the end: a 200k-file tree must be
        // refused after 2001 entries, not after listing all 200k.
        if (files.length > maxFiles) throw new FolderTooLargeError('files', maxFiles);
      }
    }
  };

  await walk(root, destRoot, 0);
  return { dirs, files, skipped };
}

/**
 * Read EVERY entry of a browser `FileSystemDirectoryReader`.
 *
 * **`readEntries` returns at most ~100 entries per call in Chromium**, and the
 * spec says to keep calling until it returns an empty batch. A single call —
 * which is what nearly every example online does — silently drops everything
 * past the first hundred files of a dropped folder, with no error anywhere.
 */
export function readAllDirectoryEntries(reader) {
  return new Promise((resolve, reject) => {
    const all = [];
    const next = () => {
      reader.readEntries((batch) => {
        if (!batch || batch.length === 0) {
          resolve(all);
          return;
        }
        all.push(...batch);
        next();
      }, reject);
    };
    next();
  });
}

/**
 * Adapt a dropped browser directory into the `list` shape `planFolder` walks,
 * so an OS drop and an in-app drag share one walk and one set of limits.
 * Paths are the entries' own `fullPath`; the File is fetched later, per file.
 */
export function browserDirectoryLister() {
  const byPath = new Map();
  const list = async (dirEntry) => {
    const entries = await readAllDirectoryEntries(dirEntry.createReader());
    return entries.map((e) => {
      byPath.set(e.fullPath, e);
      return {
        name: e.name,
        path: e, // the entry itself; `planFolder` passes it back into `list`
        isDir: e.isDirectory,
        // The browser API does not expose links; a dropped folder is a snapshot
        // the OS already resolved, so there is no loop to follow here.
        isSymlink: false,
        size: 0,
      };
    });
  };
  return { list, byPath };
}
